import React, { createContext, useContext, useState, useEffect } from "react";

// Créer le contexte
const ThemeContext = createContext(null);

// Hook personnalisé pour utiliser le contexte de thème
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(
      "useTheme doit être utilisé à l'intérieur d'un ThemeProvider",
    );
  }
  return context;
};

// Composant Provider qui gère l'état du thème
export const ThemeProvider = ({ children }) => {
  // Vérifier si window est défini pour éviter les erreurs SSR
  const isBrowser = typeof window !== "undefined";

  // Le mode sombre est maintenant le mode par défaut.
  const getInitialDarkMode = () => {
    if (!isBrowser) return true; // Le mode sombre est aussi le défaut pour le rendu côté serveur

    const storedTheme = localStorage.getItem("theme");
    // Si un thème est explicitement sauvegardé dans le localStorage, on respecte ce choix.
    if (storedTheme) {
      return storedTheme === "dark";
    }

    // Sinon, le mode sombre est appliqué par défaut, ignorant la préférence système.
    return true;
  };

  // État pour suivre le mode sombre
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  // Mettre à jour les classes CSS et stocker la préférence
  useEffect(() => {
    if (!isBrowser) return;

    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode, isBrowser]);

  // Fonction pour basculer entre les modes
  const toggleDarkMode = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  // Valeur du contexte
  const contextValue = {
    darkMode,
    toggleDarkMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Exporter le contexte par défaut
export default ThemeContext;
